import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchContent, fill, text } from '../api/content'
import {
  agreeAgain,
  fetchDancerLoginConsent,
  fetchDancerLogins,
  issueDancerLogin,
  setDancerPermission,
  versionsOf,
  withdrawDancerLogin,
  type DancerLogin,
  type DancerLoginConsent,
} from '../api/portal'

type Content = Parameters<typeof text>[0]

/**
 * Giving a dancer her own login, and taking it back (D-539).
 *
 * **The parent starts it and the parent ends it.** There is no admin door:
 * Tonya can make a dancer a student teacher and cannot hand out a child's
 * login, because consent is the parent's to give.
 *
 * **The words come first, and they are the studio's words** — content blocks
 * Tonya edits (A-16), fetched with their versions so that what is recorded is
 * what she actually read. Since messaging arrived every login is agreed to
 * (D-629): under thirteen the words the law asks for and the messaging
 * paragraph, over thirteen the messaging paragraph alone.
 *
 * **A login from before messaging is asked again, for that paragraph alone.**
 * The row says what she agreed to has changed and shows the new part; until
 * she agrees, her dancer cannot be messaged and cannot message. Nothing about
 * the login itself changes when she does.
 *
 * **Switching it off is not a punishment and the copy says so.** It stops the
 * sign-in and nothing else: her classes, her attendance and anything she is
 * helping with are untouched. A parent who thinks she might be removing her
 * daughter from a class will not touch the control at all.
 */
export default function DancerLoginPanel() {
  const queryClient = useQueryClient()

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dancer-logins'],
    queryFn: fetchDancerLogins,
  })

  const [opened, setOpened] = useState<string | null>(null)

  const done = () => {
    setOpened(null)
    queryClient.invalidateQueries({ queryKey: ['dancer-logins'] })
    queryClient.invalidateQueries({ queryKey: ['dancer-login-consent'] })
  }

  const withdraw = useMutation({ mutationFn: withdrawDancerLogin, onSuccess: done })

  return (
    <section className="mt-8">
      <h2 className="mb-1.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Their own sign-in
      </h2>

      {/*
        ⚠ **Kept true as the feature grew** (D-921). This said she "sees" her
        schedule and attendance and nothing else, which stopped being the
        whole story the moment a parent could let her act — and the screen
        where a decision is made is the worst place for a description of it
        that has quietly expired.
      */}
      <p className="mb-3 text-[13px] text-ink-3">
        A dancer with their own login sees their own schedule and their own attendance &mdash;
        only theirs, never a brother&rsquo;s or sister&rsquo;s, and nothing about your account.
        They need an email address of their own. They cannot change anything unless you say so
        below, and switching their login off takes those permissions with it.
      </p>

      {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}

      {isError && (
        <p role="alert" className="text-[13px] text-crit">
          {(error as Error).message}
        </p>
      )}

      {withdraw.error && (
        <p role="alert" className="mb-2 text-[13px] text-crit">
          {(withdraw.error as Error).message}
        </p>
      )}

      {data && data.length > 0 && (
        <div className="rounded-2xl border border-line bg-surface px-4">
          {data.map((dancer) => (
            <Row
              key={dancer.dancerId}
              dancer={dancer}
              content={content}
              open={opened === dancer.dancerId}
              busy={withdraw.isPending}
              onOpen={() => setOpened(dancer.dancerId)}
              onClose={() => setOpened(null)}
              onDone={done}
              onWithdraw={() => withdraw.mutate(dancer.dancerId)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/** What each state means, in words a parent can act on. */
function says(dancer: DancerLogin): string {
  switch (dancer.state) {
    case 'active':
      return `Signs in as ${dancer.email}`
    case 'invited':
      return `Invitation sent to ${dancer.email} — waiting for her to set a password`
    case 'withdrawn':
      return 'Switched off — she cannot sign in'
    default:
      return 'No sign-in yet — you will be asked first'
  }
}

function Row({
  dancer,
  content,
  open,
  busy,
  onOpen,
  onClose,
  onDone,
  onWithdraw,
}: {
  dancer: DancerLogin
  content: Content
  open: boolean
  busy: boolean
  onOpen: () => void
  onClose: () => void
  onDone: () => void
  onWithdraw: () => void
}) {
  const on = dancer.state === 'active' || dancer.state === 'invited'

  return (
    <div className="border-t border-line py-3 first:border-t-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 grow">
          <p className="text-[14px] font-semibold">{dancer.name}</p>
          <p className="wrap-anywhere text-[12.5px] text-ink-3">{says(dancer)}</p>
        </div>

        {on ? (
          <button
            type="button"
            disabled={busy}
            onClick={onWithdraw}
            className="py-1 text-[12.5px] font-semibold text-ink-3 underline underline-offset-2"
          >
            Switch off
          </button>
        ) : (
          !open && (
            <button
              type="button"
              onClick={onOpen}
              className="py-1 text-[12.5px] font-semibold text-accent underline underline-offset-2"
            >
              {dancer.state === 'withdrawn' ? 'Switch back on' : 'Set one up'}
            </button>
          )
        )}
      </div>

      {on && <Switches dancer={dancer} onDone={onDone} />}

      {on && dancer.needsReconsent && <Reconsent dancer={dancer} content={content} onDone={onDone} />}

      {open && <Setup dancer={dancer} content={content} onCancel={onClose} onDone={onDone} />}
    </div>
  )
}

/**
 * What her login may *do*, beyond reading her own record (D-920, D-921).
 *
 * ⚠ **Two switches, divided on money.** `asked`, choosing it over one:
 * *"most parents would let a fourteen-year-old say she is sick without
 * letting her commit them to paid lessons."* Everything free is one switch;
 * asking a teacher for a private is the other.
 *
 * **Both off by default** — *"it definitely be off by default"* — so a dancer
 * with a login reads her attendance and her standing and changes none of it
 * until her parent says otherwise.
 *
 * *Shown only while the login is on: a withdrawn login takes its switches
 * with it, so offering them here would be offering a control about an account
 * nobody can sign in to.*
 */
function Switches({ dancer, onDone }: { dancer: DancerLogin; onDone: () => void }) {
  const [problem, setProblem] = useState<string | null>(null)

  const set = useMutation({
    mutationFn: ({ permission, allowed }: { permission: 'attendance' | 'privates'; allowed: boolean }) =>
      setDancerPermission(dancer.dancerId, permission, allowed),
    onSuccess: () => {
      setProblem(null)
      onDone()
    },
    onError: (e) => setProblem((e as Error).message),
  })

  const rows = [
    /*
     * ⚠ **Not every dancer here is a girl**, and copy that assumes one is
     * reads as being about somebody else's child. `asked`, and not for the
     * first time. The dancer's own first name carries most of it; where a
     * pronoun is unavoidable it is *they*.
     */
    {
      permission: 'attendance' as const,
      on: dancer.mayArrangeAttendance,
      label: `Let ${dancer.firstName} manage their own classes`,
      says: 'Tell the studio they will be out, claim a makeup, bank a class.',
    },
    {
      permission: 'privates' as const,
      on: dancer.mayBookPrivates,
      label: `Let ${dancer.firstName} book private lessons`,
      says: 'They can ask a teacher for a private. You pay the teacher, and we tell you when they ask.',
    },
  ]

  return (
    <div className="mt-2.5 flex flex-col gap-2 rounded-lg bg-surface-2 px-3 py-2.5">
      {rows.map((row) => (
        <label key={row.permission} className="flex items-start gap-2.5">
          {/*
            A checkbox rather than a pair of buttons: this is one setting with
            an obvious off state, and the sentence under it says what the off
            state costs her.
          */}
          <input
            type="checkbox"
            checked={row.on}
            disabled={set.isPending}
            onChange={(e) => set.mutate({ permission: row.permission, allowed: e.target.checked })}
            className="mt-0.5 size-4 shrink-0 accent-accent"
          />

          <span className="min-w-0">
            <span className="block text-[13px] font-semibold wrap-anywhere">{row.label}</span>
            <span className="block text-[12.5px] text-ink-3 wrap-anywhere">{row.says}</span>
          </span>
        </label>
      ))}

      {problem && <p className="text-[12.5px] text-crit">{problem}</p>}
    </div>
  )
}

/** The words, block by block, and the tick that says she read them. */
function Words({
  dancer,
  content,
  consent,
  agreed,
  onAgreed,
}: {
  dancer: DancerLogin
  content: Content
  consent: DancerLoginConsent
  agreed: boolean
  onAgreed: (agreed: boolean) => void
}) {
  return (
    <div className="mt-3">
      {consent.blocks.map((block) => (
        <p key={block.key} className="mt-2 text-[12.5px] whitespace-pre-line text-ink-2 first:mt-0">
          {block.body}
        </p>
      ))}

      <label className="mt-2 flex items-start gap-2 text-[12.5px] font-semibold">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => onAgreed(e.target.checked)}
          className="mt-0.5"
        />
        {fill(content, 'portal.dancerLogin.agree', { firstName: dancer.firstName }) ||
          `I am ${dancer.firstName}'s parent or guardian and I agree to this`}
      </label>
    </div>
  )
}

/**
 * Asked again (D-629): what she agreed to no longer covers what the app does,
 * so the new part is shown and a fresh agreement taken. Only the new part -
 * the server sends the blocks her live consent does not cover, never the
 * whole text again.
 */
function Reconsent({ dancer, content, onDone }: { dancer: DancerLogin; content: Content; onDone: () => void }) {
  const [agreed, setAgreed] = useState(false)

  const consent = useQuery({
    queryKey: ['dancer-login-consent', dancer.dancerId],
    queryFn: () => fetchDancerLoginConsent(dancer.dancerId),
  })

  const agree = useMutation({
    mutationFn: () => agreeAgain(dancer.dancerId, versionsOf(consent.data!)),
    onSuccess: onDone,
  })

  return (
    <div className="mt-3 rounded-xl border border-warn bg-warn-bg p-3" role="region" aria-label={dancer.name}>
      <p className="text-[13px] font-semibold">
        {text(content, 'portal.dancerLogin.reconsent', 'What you agreed to has changed. Please read the new part and agree again.')}
      </p>

      {consent.data && (
        <Words dancer={dancer} content={content} consent={consent.data} agreed={agreed} onAgreed={setAgreed} />
      )}

      {consent.isError && (
        <p role="alert" className="mt-2 text-[13px] text-crit">
          {(consent.error as Error).message}
        </p>
      )}

      {agree.error && (
        <p role="alert" className="mt-2 text-[13px] text-crit">
          {(agree.error as Error).message}
        </p>
      )}

      <button
        type="button"
        disabled={!consent.data || !agreed || agree.isPending}
        onClick={() => agree.mutate()}
        className="mt-3 rounded-lg bg-accent px-3 py-1.5 text-[13px] font-semibold text-on-accent disabled:opacity-50"
      >
        {text(content, 'portal.dancerLogin.reconsent.agree', 'I agree')}
      </button>
    </div>
  )
}

/**
 * The address, and the agreement.
 *
 * **The words are fetched before anything is created**, and their versions
 * come back with them and are sent on. A parent cannot agree to one wording
 * and have a different one recorded against her name.
 */
function Setup({
  dancer,
  content,
  onCancel,
  onDone,
}: {
  dancer: DancerLogin
  content: Content
  onCancel: () => void
  onDone: () => void
}) {
  const [email, setEmail] = useState(dancer.email ?? '')
  const [agreed, setAgreed] = useState(false)

  const consent = useQuery({
    queryKey: ['dancer-login-consent', dancer.dancerId],
    queryFn: () => fetchDancerLoginConsent(dancer.dancerId),
  })

  const send = useMutation({
    mutationFn: () => issueDancerLogin(dancer.dancerId, email, consent.data ? versionsOf(consent.data) : null),
    onSuccess: onDone,
  })

  // Waiting on the words: every login is agreed to now (D-629), so a parent
  // must not be able to submit before they have arrived - and an answer with
  // no words to show is one to wait on, never one to send past.
  const waiting = consent.isPending || consent.data === null
  const blocked = waiting || !agreed || email.trim().length === 0

  return (
    <div className="mt-3 rounded-xl bg-surface-2 p-3">
      <label className="block text-[12.5px] font-semibold" htmlFor={`email-${dancer.dancerId}`}>
        {dancer.firstName}&rsquo;s email address
      </label>

      <input
        id={`email-${dancer.dancerId}`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="her.own@example.com"
        className="mt-1 w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-[14px]"
      />

      {consent.data && (
        <Words dancer={dancer} content={content} consent={consent.data} agreed={agreed} onAgreed={setAgreed} />
      )}

      {consent.isError && (
        <p role="alert" className="mt-2 text-[13px] text-crit">
          {(consent.error as Error).message}
        </p>
      )}

      {send.error && (
        <p role="alert" className="mt-2 text-[13px] text-crit">
          {(send.error as Error).message}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={blocked || send.isPending}
          onClick={() => send.mutate()}
          className="rounded-lg bg-accent px-3 py-1.5 text-[13px] font-semibold text-on-accent disabled:opacity-50"
        >
          Send her an invitation
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1.5 text-[13px] font-semibold text-ink-3 underline underline-offset-2"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
